/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                        useAccessLevel Hook                                 ║
 * ║            SINGLE SOURCE OF TRUTH FOR FRONTEND ACCESS CONTROL             ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║                                                                           ║
 * ║  This hook fetches access level from /api/subscriptions/access/level      ║
 * ║  which internally uses accessManager.getAccessLevel() on the backend.     ║
 * ║                                                                           ║
 * ║  USE THIS HOOK for all access-related decisions in the frontend.          ║
 * ║  DO NOT manually check subscription_status or premium_until.              ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  USAGE:                                                                   ║
 * ║                                                                           ║
 * ║  const {                                                                  ║
 * ║    loading,                // true while fetching                         ║
 * ║    hasAccess,              // boolean - can user access premium content?  ║
 * ║    state,                  // subscription state (TRIALING, ACTIVE, etc.) ║
 * ║    isTrialing,             // is user in trial period?                    ║
 * ║    trialDaysRemaining,     // days left in trial (or null)                ║
 * ║    requiresOnboarding,     // needs to complete payment setup?            ║
 * ║    showBanner,             // should we show a warning banner?            ║
 * ║    bannerMessage,          // warning message to display                  ║
 * ║    userColor,              // 'blue' or 'red' (for admin inbox context)   ║
 * ║    actions,                // available actions ['cancel', 'reactivate']  ║
 * ║    refetch                 // function to manually refresh                ║
 * ║  } = useAccessLevel()                                                     ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  CACHING:                                                                 ║
 * ║  - Results are cached for 30 seconds to reduce API calls                  ║
 * ║  - Call clearAccessLevelCache() on logout to reset                        ║
 * ║  - Call refetch() to force refresh                                        ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  USED BY:                                                                 ║
 * ║  - ProtectedRoute.tsx (route protection)                                  ║
 * ║  - WelcomeScreen.tsx (onboarding flow)                                    ║
 * ║  - Any component that needs to check user access                          ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useCallback, useRef } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const CACHE_DURATION_MS = 30000 // Cache for 30 seconds

/**
 * Access level response from backend
 */
export interface AccessLevel {
  // Core access info
  hasAccess: boolean
  state: string
  source: string | null

  // Tier info
  tier: string

  // Timing info
  expiresAt: string | null
  trialDaysRemaining: number | null
  premiumDaysRemaining: number | null
  graceDaysRemaining: number | null  // NEW: Days remaining in grace period

  // Onboarding requirements
  requiresOnboarding: boolean
  requiresPaymentSetup: boolean
  redirectTo: string | null

  // UI display
  userColor: 'blue' | 'red'
  showBanner: boolean
  bannerMessage: string | null
  bannerType: 'info' | 'warning' | 'error'
  badge: string | null

  // Available actions
  actions: string[]

  // Warnings/prompts
  warnings: string[]

  // Legacy status
  isLegacy: boolean
  showUpgradePrompt: boolean
  upgradeMessage: string | null

  // Metadata
  metadata: {
    subscriptionId: string | null
    stripeSubscriptionId: string | null
    inviteId: string | null
    entrySource: string | null
    paymentDeferredAt: string | null
  }

  // Request timestamp
  timestamp: string
}

/**
 * Hook return value
 */
export interface UseAccessLevel {
  // Status
  loading: boolean
  error: string | null

  // Access data
  accessLevel: AccessLevel | null
  hasAccess: boolean
  requiresOnboarding: boolean

  // State info
  state: string
  source: string | null
  tier: string

  // Trial info
  isTrialing: boolean
  trialDaysRemaining: number | null
  graceDaysRemaining: number | null  // Days remaining in grace period
  expiresAt: string | null

  // Display
  userColor: 'blue' | 'red'
  showBanner: boolean
  bannerMessage: string | null
  bannerType: string

  // Actions
  actions: string[]

  // Legacy
  isLegacy: boolean
  showUpgradePrompt: boolean

  // Methods
  refresh: () => Promise<void>
}

// Module-level cache to share between hook instances
let cachedAccessLevel: AccessLevel | null = null
let cacheTimestamp: number = 0
let fetchPromise: Promise<AccessLevel | null> | null = null

/**
 * useAccessLevel Hook
 *
 * @param options.skip - Skip fetching (useful when user is not authenticated)
 * @param options.refreshInterval - Auto-refresh interval in ms (default: none)
 */
export function useAccessLevel(options?: {
  skip?: boolean
  refreshInterval?: number
}): UseAccessLevel {
  const { skip = false, refreshInterval } = options || {}

  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(cachedAccessLevel)
  const [loading, setLoading] = useState(!cachedAccessLevel && !skip)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)

  /**
   * Fetch access level from API
   * @param isRetry - If true, this is a retry after token refresh (prevents infinite loops)
   */
  const fetchAccessLevel = useCallback(async (isRetry: boolean = false): Promise<AccessLevel | null> => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.log('[useAccessLevel] No auth token found')
      return null
    }

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/access/level`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.status === 401) {
        // If this is already a retry, don't retry again (prevents infinite loops)
        if (isRetry) {
          console.warn('[useAccessLevel] Unauthorized after retry - triggering logout')
          // Token is truly invalid - clear session and force re-login
          if (window.__authLogout) {
            window.__authLogout()
          }
          return null
        }

        console.warn('[useAccessLevel] Unauthorized - attempting token refresh')

        // Try to refresh token before giving up
        const currentToken = localStorage.getItem('auth_token')
        if (currentToken) {
          try {
            const refreshResponse = await fetch(`${API_URL}/api/auth/refresh-token`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${currentToken}`,
                'Content-Type': 'application/json'
              }
            })

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json()
              localStorage.setItem('auth_token', refreshData.token)
              if (refreshData.user) {
                localStorage.setItem('user_data', JSON.stringify(refreshData.user))
              }
              console.log('[useAccessLevel] Token refreshed, retrying access level fetch')
              // Retry the access level fetch with new token (isRetry=true prevents further retries)
              return fetchAccessLevel(true)
            }
          } catch (refreshErr) {
            console.warn('[useAccessLevel] Token refresh failed - triggering logout:', refreshErr)
            // Token refresh failed - clear session and force re-login
            if (window.__authLogout) {
              window.__authLogout()
            }
            return null
          }
        }

        // Refresh failed or no token - trigger logout and return null
        console.warn('[useAccessLevel] No valid token - triggering logout')
        if (window.__authLogout) {
          window.__authLogout()
        }
        return null
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[useAccessLevel] API error:', response.status, errorData)
        throw new Error(errorData.message || 'Failed to fetch access level')
      }

      const data: AccessLevel = await response.json()

      // Update cache
      cachedAccessLevel = data
      cacheTimestamp = Date.now()

      return data
    } catch (err) {
      console.error('[useAccessLevel] Fetch error:', err)
      throw err
    }
  }, [])

  /**
   * Refresh access level (bypass cache)
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (skip) return

    setLoading(true)
    setError(null)

    try {
      // Clear cache to force fresh fetch
      cachedAccessLevel = null
      cacheTimestamp = 0
      fetchPromise = null

      const data = await fetchAccessLevel()
      if (mountedRef.current) {
        setAccessLevel(data)
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [skip, fetchAccessLevel])

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    // Reset mounted ref at start of effect (in case cleanup ran previously)
    mountedRef.current = true

    if (skip) {
      setLoading(false)
      return
    }

    // Check if cache is still valid
    const isCacheValid = cachedAccessLevel && (Date.now() - cacheTimestamp < CACHE_DURATION_MS)

    if (isCacheValid) {
      setAccessLevel(cachedAccessLevel)
      setLoading(false)
      return
    }

    // If there's already a fetch in progress, wait for it
    if (fetchPromise) {
      fetchPromise
        .then(data => {
          if (mountedRef.current) {
            setAccessLevel(data)
          }
          setLoading(false)
        })
        .catch(err => {
          if (mountedRef.current) {
            setError(err instanceof Error ? err.message : 'Unknown error')
          }
          setLoading(false)
        })
      return
    }

    // Start new fetch
    setLoading(true)
    fetchPromise = fetchAccessLevel()

    fetchPromise
      .then(data => {
        if (mountedRef.current) {
          setAccessLevel(data)
        }
      })
      .catch(err => {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Unknown error')
        }
      })
      .finally(() => {
        fetchPromise = null
        // Always set loading to false when fetch completes
        setLoading(false)
      })

    return () => {
      mountedRef.current = false
    }
  }, [skip, fetchAccessLevel])

  /**
   * Auto-refresh interval
   */
  useEffect(() => {
    if (!refreshInterval || skip) return

    const timer = setInterval(() => {
      refresh()
    }, refreshInterval)

    return () => clearInterval(timer)
  }, [refreshInterval, skip, refresh])

  // Derive values from accessLevel
  const isTrialing = accessLevel?.state === 'TRIALING' || accessLevel?.state === 'TRIAL_ENDING_SOON' || accessLevel?.state === 'DEFERRED_TRIAL'

  return {
    // Status
    loading,
    error,

    // Access data
    accessLevel,
    hasAccess: accessLevel?.hasAccess ?? false,
    requiresOnboarding: accessLevel?.requiresOnboarding ?? false,

    // State info
    state: accessLevel?.state ?? 'UNKNOWN',
    source: accessLevel?.source ?? null,
    tier: accessLevel?.tier ?? 'none',

    // Trial info
    isTrialing,
    trialDaysRemaining: accessLevel?.trialDaysRemaining ?? null,
    graceDaysRemaining: accessLevel?.graceDaysRemaining ?? null,
    expiresAt: accessLevel?.expiresAt ?? null,

    // Display
    userColor: accessLevel?.userColor ?? 'red',
    showBanner: accessLevel?.showBanner ?? false,
    bannerMessage: accessLevel?.bannerMessage ?? null,
    bannerType: accessLevel?.bannerType ?? 'info',

    // Actions
    actions: accessLevel?.actions ?? [],

    // Legacy
    isLegacy: accessLevel?.isLegacy ?? false,
    showUpgradePrompt: accessLevel?.showUpgradePrompt ?? false,

    // Methods
    refresh
  }
}

/**
 * Clear the access level cache
 * Call this when user logs out or subscription changes
 */
export function clearAccessLevelCache(): void {
  cachedAccessLevel = null
  cacheTimestamp = 0
  fetchPromise = null
}

export default useAccessLevel
